import { useEffect, useRef, type JSX, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AlertCircle, FolderOpen, Loader2, RotateCcw } from "lucide-react";
import type { AdminMediaResponse } from "@ckgo2/shared/types";
import { uiLocale } from "@/i18n";
import { shouldLoadMore, type MediaGridState } from "@/hooks/useMediaLibrary";
import PickerTile from "./PickerTile";
import type { PickerTileState } from "./pickerModel";

/**
 * Mřížka okna výběru fotek (řez 05, Kontrakt bod 3): čistý pohled bez hooků
 * (`PickerGridView`, precedent `MediaGridView`) a kontejner s pozorovatelem donačítání
 * (`PickerGrid`). Pozorovatel má kořen v posouvacím prvku okna `[data-media-picker-scroll]`
 * (kreslí ho `MediaPicker`, K2) a po skončení obnovy (`fetching`) se znovu přihlásí.
 *
 * Větve stavů jsou pomocné funkce volané jako funkce, ne vnořené komponenty: test volá
 * `PickerGridView` jako funkci a ve vráceném stromu hledá tlačítka podle háčku. Hlavička nemá
 * „Vybrat vše načtené“ (okno vybírá klikem v pořadí). Každé tlačítko je `type="button"`:
 * okno leží uvnitř `<form>` editoru.
 */

/** Jako `MediaGrid`, o sloupec méně na `xl`: okno je užší než stránka Média. */
const GRID_CLASS =
  "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-5";
const SKELETON_TILES = 12;

export interface PickerGridViewProps {
  state: MediaGridState;
  items: readonly AdminMediaResponse[];
  total: number | null;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError: boolean;
  activeFilterCount: number;
  stateOf: (id: string) => PickerTileState;
  onActivate: (id: string) => void;
  onOpenViewer: (id: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onClearFilters: () => void;
  onGoUpload: () => void;
  /** Okno otevřené z hero místa (řez 09, F8.6): dlaždice fotek na výšku nesou štítek. */
  purpose?: "hero";
  /** Sentinel donačítání; dává ho kontejner (čistý pohled nevolá useRef, precedent MediaGridViewProps). */
  sentinelRef: RefObject<HTMLDivElement | null>;
  t: TFunction;
}

function renderSkeleton(): JSX.Element {
  return (
    <div data-media-picker-skeleton="" aria-busy="true" className={GRID_CLASS}>
      {Array.from({ length: SKELETON_TILES }, (_, i) => (
        <div
          key={i}
          className="animate-pulse overflow-hidden rounded-lg border-2 border-transparent"
        >
          <div className="aspect-square bg-gray-200 dark:bg-gray-800" />
          <div className="space-y-1 p-1.5">
            <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-2.5 w-1/2 rounded bg-gray-100 dark:bg-gray-800/60" />
          </div>
        </div>
      ))}
    </div>
  );
}

function renderError({ t, onRetry }: PickerGridViewProps): JSX.Element {
  return (
    <div
      role="alert"
      className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
    >
      <AlertCircle className="h-8 w-8" />
      <p className="text-sm font-medium">{t("media.page.loadErrorShort")}</p>
      <button
        type="button"
        data-media-picker-retry=""
        onClick={onRetry}
        className="btn-secondary text-sm"
      >
        <RotateCcw className="h-4 w-4" /> {t("common.actions.retry")}
      </button>
    </div>
  );
}

/** Řádek „N fotek“ nad mřížkou i nad prázdným rámečkem (háček s `data-count`). */
function renderTotal(count: number, t: TFunction): JSX.Element {
  return (
    <p
      data-media-picker-total=""
      data-count={count}
      className="text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {t("media.page.total", {
        count,
        formatted: new Intl.NumberFormat(uiLocale()).format(count),
      })}
    </p>
  );
}

function renderEmpty({
  activeFilterCount,
  t,
  onClearFilters,
  onGoUpload,
}: PickerGridViewProps): JSX.Element {
  const filtered = activeFilterCount > 0;
  return (
    <div className="space-y-3">
      {renderTotal(0, t)}
      <div
        data-media-picker-empty=""
        className="flex h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-gray-300 text-gray-500 dark:border-gray-700"
      >
        <FolderOpen className="h-14 w-14 text-gray-300" />
        <p className="text-lg font-medium">
          {filtered ? t("mediaPicker.empty.filtered") : t("mediaPicker.empty.none")}
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {filtered && (
            <button
              type="button"
              data-media-picker-clear-filters=""
              onClick={onClearFilters}
              className="btn-secondary text-sm"
            >
              {t("mediaPicker.empty.clear")}
            </button>
          )}
          <button
            type="button"
            data-media-picker-go-upload=""
            onClick={onGoUpload}
            className="btn-secondary text-sm"
          >
            {t("mediaPicker.empty.upload")}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderItems({
  items,
  total,
  loadingMore,
  loadMoreError,
  stateOf,
  sentinelRef,
  t,
  onActivate,
  onOpenViewer,
  onLoadMore,
  purpose,
}: PickerGridViewProps): JSX.Element {
  return (
    <div className="space-y-3">
      {renderTotal(total ?? items.length, t)}

      <div className={GRID_CLASS}>
        {items.map((m) => (
          <PickerTile
            key={m.id}
            item={m}
            state={stateOf(m.id)}
            t={t}
            onActivate={onActivate}
            onOpenViewer={onOpenViewer}
            purpose={purpose}
          />
        ))}
      </div>

      <div ref={sentinelRef} aria-hidden="true" className="h-px" />
      {loadingMore && (
        <div className="flex justify-center py-4">
          <Loader2 className="text-primary-500 h-6 w-6 animate-spin" />
        </div>
      )}
      {loadMoreError && (
        <div
          role="alert"
          className="flex items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          <span>{t("media.page.loadErrorShort")}</span>
          {/* Po chybě další stránky se donačítá znovu (fetchNextPage), ne obnoví celý seznam. */}
          <button
            type="button"
            data-media-picker-retry=""
            onClick={onLoadMore}
            className="btn-secondary text-sm"
          >
            <RotateCcw className="h-4 w-4" /> {t("common.actions.retry")}
          </button>
        </div>
      )}
    </div>
  );
}

export function PickerGridView(props: PickerGridViewProps): JSX.Element {
  switch (props.state) {
    case "loading":
      return renderSkeleton();
    case "error":
      return renderError(props);
    case "empty":
      return renderEmpty(props);
    case "items":
      return renderItems(props);
  }
}

/**
 * fetching = library.isFetching (načítá se cokoli); stráž shouldLoadMore proti donačtení během obnovy.
 *
 * Kořen pozorovatele je posouvací prvek okna `[data-media-picker-scroll]`: okno je `fixed`
 * a posouvá se samo, `<main>` layoutu pod ním (kořen `MediaGrid`) by výhled neměnil.
 * `IntersectionObserver` hlásí jen změnu průniku, proto se pozorovatel vytvoří znovu po každé
 * změně počtu položek a stavu načítání (`observe` doručí výchozí stav; jinak by se donačítání
 * zastavilo se sentinelem ve výhledu).
 */
export default function PickerGrid({
  fetching,
  ...view
}: Omit<PickerGridViewProps, "t" | "sentinelRef"> & { fetching: boolean }): JSX.Element {
  const { t } = useTranslation();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { state, hasMore, loadMoreError, onLoadMore } = view;
  const itemsLength = view.items.length;

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (state !== "items" || !sentinel || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const isIntersecting = entries.some((e) => e.isIntersecting);
        if (
          shouldLoadMore({
            isIntersecting,
            hasNextPage: hasMore,
            isFetching: fetching,
            isFetchNextPageError: loadMoreError,
          })
        ) {
          onLoadMore();
        }
      },
      { root: sentinel.closest("[data-media-picker-scroll]") ?? null, rootMargin: "800px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [state, itemsLength, hasMore, fetching, loadMoreError, onLoadMore]);

  return <PickerGridView {...view} t={t} sentinelRef={sentinelRef} />;
}
